import {
  categoriesSelections,
  PlanWizardCategoryEnum,
} from '@chiroup/core/constants/SOAPPreferences';
import { Condition } from '@chiroup/core/types/Condition.type';
import { CarePlanComplaint, Visit } from '@chiroup/core/types/PatientVisit.type';
import { seedUnifiedObjectiveFromCondition } from './sandboxObjectiveSeeding';

/** The sandbox picker uses system-default negative E&M IDs. Supply their
 * output templates only to the note builder; keeping this out of encounter
 * state preserves the Joints → CMT behavior that requires no wizard config. */
export const applySandboxPlanTemplates = (visitForm: Visit): Visit => {
  if (visitForm.planWizardCategory) return visitForm;

  return {
    ...visitForm,
    planWizardCategory: [
      {
        id: 'sandbox-em',
        name: PlanWizardCategoryEnum.EvaluationManagement,
        visible: true,
        collapsed: false,
        autoSalt: false,
        selections: categoriesSelections[PlanWizardCategoryEnum.EvaluationManagement].map(
          selection => ({
            ...selection,
            ID: selection.id ?? selection.baseId,
          })
        ),
      },
    ],
  };
};

/**
 * Spec (Story 5, Review & Sign): the note's Myofascial findings come from the
 * muscles the user selected in the Objective module — UNLESS none were
 * selected there, in which case the selected conditions' stock protocols are
 * the reference. (Production always renders from the objective module; the
 * fallback is sandbox-specific behavior, applied only to the copy of the
 * visit used for note rendering — the walkthrough state is never mutated.)
 */
export const applyMyofascialProtocolFallback = (
  visitForm: Visit,
  conditionsById: Record<number, Partial<Condition>>
): Visit => {
  const hasUserMyofascial =
    Object.keys(visitForm.unifiedObjective?.Myofascial?.detail || {}).length > 0;

  if (hasUserMyofascial) return visitForm;

  const complaints = visitForm.encounterInfo?.complaints || [];
  let unifiedObjective = visitForm.unifiedObjective;

  complaints.forEach(complaint => {
    complaint.assessment?.conditions?.forEach(assessmentCondition => {
      const condition = conditionsById[assessmentCondition.conditionId];
      if (!condition?.muscles?.length) return;

      unifiedObjective = seedUnifiedObjectiveFromCondition(
        unifiedObjective,
        { muscles: condition.muscles },
        complaint.id || ''
      );
    });
  });

  if (unifiedObjective === visitForm.unifiedObjective) return visitForm;

  return { ...visitForm, unifiedObjective };
};

/**
 * Round-2 tester note #9: the intake surveys appended to the note render in a
 * single color. buildCompletedSurvey bakes the EHR's answer colors (green
 * reports / gray denies) into inline styles; strip just those color
 * declarations, sandbox-side, so everything else about the shared builder's
 * markup (bold headers, indentation) survives untouched.
 */
/**
 * Round-3 tester note #5: the note's Joint involvement should read as the
 * clinical sentence ("Spine joint dysfunction, characterized by hypomobility
 * and palpable asymmetry ... is present at: T6, L1"), not a bare segment list.
 *
 * That sentence is already the shared builder's own wording — getJointContent
 * emits it for every joint area EXCEPT when there is neither prior-visit data
 * nor an improvement rating, which is exactly the sandbox's first-visit shape,
 * so it falls to the terse list form instead.
 *
 * Rather than restate clinical wording sandbox-side (where it could drift from
 * the EHR's), mark each documented area as present last visit on a RENDER-ONLY
 * copy of the visit, which is the one input that selects the prose form. The
 * markers carry no findings, so nothing is compared against them, and the
 * walkthrough's own state is never touched. `lastVisitUnifiedObjective` is read
 * only by the note builders — no UI reads it — and each objective section reads
 * its own key, so seeding Joints cannot affect ROM, Myofascial, or Orthopedic.
 *
 * The improvement rating stays unset, so the sentence renders without any
 * "as compared with previous visit" clause.
 */
export const applyJointDysfunctionProse = (visitForm: Visit): Visit => {
  const areas = Object.keys(visitForm.unifiedObjective?.Joints?.detail || {});
  if (!areas.length) return visitForm;

  return {
    ...visitForm,
    lastVisitUnifiedObjective: {
      ...visitForm.lastVisitUnifiedObjective,
      Joints: {
        ...visitForm.lastVisitUnifiedObjective?.Joints,
        detail: areas.reduce(
          (detail: Record<string, Record<string, never>>, area) => {
            detail[area] = {};

            return detail;
          },
          { ...(visitForm.lastVisitUnifiedObjective?.Joints?.detail || {}) }
        ),
      },
    },
  } as Visit;
};

/**
 * Round-3 tester note #6: the Assessment listed the diagnoses but no ICD
 * codes. The builder takes those codes from each stored assessment condition's
 * `referenceCodes`, which the diagnosis step copies off the condition-library
 * row — so a library payload without codes (the basicList variant caches under
 * the same key, and carries none) leaves the note with nothing to print.
 *
 * Review & Sign already fetches full condition records for the note, and that
 * lightweight fetch does return referenceCodes, so backfill from it on a
 * RENDER-ONLY copy. Favorited codes win, else the primary code in saved order —
 * the same rule the diagnosis step applies when committing the encounter.
 */
export const applyAssessmentCodeFallback = (
  complaints: CarePlanComplaint[],
  conditionsById: Record<number, Partial<Condition>>
): CarePlanComplaint[] => {
  let changed = false;

  const next = complaints.map(complaint => {
    const conditions = complaint.assessment?.conditions;
    if (!conditions?.length) return complaint;

    const repaired = conditions.map(condition => {
      if (condition.referenceCodes?.length) return condition;

      const available = [...(conditionsById[condition.conditionId]?.referenceCodes || [])].sort(
        (a, b) => (a.order || Number.MAX_SAFE_INTEGER) - (b.order || Number.MAX_SAFE_INTEGER)
      );
      if (!available.length) return condition;

      const favorited = available.filter(code => code.favorite === 1);
      changed = true;

      return { ...condition, referenceCodes: favorited.length ? favorited : available.slice(0, 1) };
    });

    if (repaired.every((condition, i) => condition === conditions[i])) return complaint;

    return { ...complaint, assessment: { ...complaint.assessment, conditions: repaired } };
  });

  return changed ? next : complaints;
};

export const stripSurveyAnswerColors = (html: string): string =>
  html.replace(/\s*color:\s*#(?:93cc68|9CA3AF);*/gi, '');

/**
 * Round-3 tester note #4: tidy the appended surveys' Review-of-Systems style
 * formatting — emphasize the system headers (General, Nervous system, ...)
 * and put the positives on the "Patient reports:" line instead of a bulleted
 * list beneath it, matching how "Patient denies:" already reads.
 *
 * Applied sandbox-side: buildCompletedSurvey formats notes for every clinic,
 * so its markup stays untouched. Both surfaces get this, because the preview
 * editors and the signed note render the same generated HTML.
 */
export const tidySurveySections = (html: string): string =>
  html
    // Collapse "Patient reports:" + <ul><li>..</li></ul> onto one line, in the
    // same shape the builder already emits for "Patient denies:".
    .replace(
      /<p style="margin-left: 16px; font-weight: bold;">Patient reports:<\/p>\s*<ul[^>]*>([\s\S]*?)<\/ul>/g,
      (_match, items: string) => {
        const values = [...items.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/g)]
          .map(item => item[1].trim())
          .filter(Boolean)
          .join(', ');

        return `<p style="margin-left: 16px;"><span style="font-weight: bold;">Patient reports:</span> ${values}</p>`;
      }
    )
    // System headers are the builder's only attribute-less, tag-free
    // paragraphs: a checkbox question renders <p>General</p>, while radio
    // questions wrap their answer in a <span> and label paragraphs carry
    // style attributes — so neither is matched here.
    .replace(/<p>([^<]+)<\/p>/g, '<p style="font-weight: bold; font-size: 1.1em;">$1</p>');
